import { useEffect, useMemo, useState } from "react"
import { usePostRequest } from "../../../../../../api/helper"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import toast from "react-hot-toast"
import { MRT_Cell, MRT_ColumnDef, MRT_RowSelectionState } from "material-react-table"
import { cloneDeep } from "lodash"
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import Loader from "../../../../../components/Loader/Loader"
import ArgosTextField from "../../../../../components/Form/TextField/ArgosTextField"
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material"
import { AdasProgramsReportPages } from "../constants"
import {ArgosTableSimple} from "../../../../../components/Table/ArgosTableSimple";


interface SelectFormatsAndNamesProps {
  page: AdasProgramsReportPages
  multiSelect: boolean
  dataSets: MRT_RowSelectionState
  selectedObjects: MRT_RowSelectionState
  reportName: string
  setReportName: React.Dispatch<React.SetStateAction<string>>
  reportFormat: string
  setReportFormat: React.Dispatch<React.SetStateAction<string>>
  selectedReports: {[reportFormat: string]: string}
  setSelectedReports: React.Dispatch<React.SetStateAction<{[reportFormat: string]: string}>>
}

export const SelectFormatsAndNames = (props: SelectFormatsAndNamesProps) => {
  const {
    page, multiSelect, dataSets, selectedObjects, reportName, setReportName,
    reportFormat, setReportFormat, selectedReports, setSelectedReports
  } = props
  const datasetId = Object.keys(dataSets)[0]

  /**
   * Fetch report formats for selected line and objects
   */
  const fetchReportFormatsHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchReportFormatsEndpoint)
  const [isLoadingGetReportFormats, setIsLoadingGetReportFormats] = useState(false)
  const [reportFormats, setReportFormats] = useState<string[]>([])
  const [selectFormatAndNameTableData, setSelectFormatAndNameTableData] = useState<Row[]>([])

  /** One data set must be selected */
  const isSelectDatasetValid = Object.keys(dataSets).length === 1

  /** At least one object must be selected */
  const isSelectObjectsValid = Object.keys(selectedObjects).length > 0

  /** To fetch report formats, `Select data sets` and `Select objects` steps must be valid */
  const canFetchReportFormats = isSelectDatasetValid && isSelectObjectsValid

  const fetchReportFormats = () => {
    if (canFetchReportFormats) {
      setIsLoadingGetReportFormats(true)
      const body = {page, datasetId, selectedObjects}
      fetchReportFormatsHandle(body).then((res) => {
        setReportFormats(res.reportFormats)
        // Don't overwrite the name if it is already set.
        setSelectFormatAndNameTableData(prevData => {
          // Create a map for quick lookup of existing names
          const nameMap = new Map(prevData.map(row => [row.format, row.name]))
          return res.reportFormats.map((format: string) => ({
            format,
            name: nameMap.get(format) && nameMap.get(format) !== '' ? nameMap.get(format)! : ''
          }))
        })
      }).catch((err) => {
        console.log(err)
        toast.error('An error occurred while fetching the report formats')
      }).finally(() => {
        setIsLoadingGetReportFormats(false)
      })
    }
  }

  /**
   * Fetch report formats when data set or object selection changes
   */
  useEffect(() => {
    fetchReportFormats()
  }, [dataSets, selectedObjects])

  const handleReportFormatChange = (event: any) => {
    setReportFormat(event.target.value)
  }

  interface Row {
    format: string
    name: string
  }

  const handleSaveCell = (cell: MRT_Cell<Row>, value: any) => {
    const _rowsData = cloneDeep(selectFormatAndNameTableData)
    _rowsData[cell.row.index][cell.column.id as keyof Row] = value
    setSelectFormatAndNameTableData(_rowsData)
  }

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

  /**
   * Reset the report names of rows that are not selected
   */
  useEffect(() => {
    const _rowData = cloneDeep(selectFormatAndNameTableData)
    selectFormatAndNameTableData.forEach((data: any, index: number) => {
      if (!Object.keys(rowSelection).includes(index.toString())) {
        _rowData[index].name = ''
      }
    })
    setSelectFormatAndNameTableData(_rowData)
  }, [rowSelection])

  /**
   * Update selectedReports when selectFormatAndNameTableData changes
   */
  useEffect(() => {
    const _selectedReports: any = {}
    selectFormatAndNameTableData.forEach((row: any, index: number) => {
      if (Object.keys(rowSelection).includes(index.toString())) {
        _selectedReports[row.format] = row.name
      }
    })
    setSelectedReports(_selectedReports)
  }, [selectFormatAndNameTableData])

  const selectFormatAndNameTableColumns = useMemo<MRT_ColumnDef<Row>[]>(
    () => [
      {
        accessorKey: "format",
        header: "Report Format",
        enableEditing: false,
      },
      {
        accessorKey: "name",
        header: "Report Name",
        grow: true,
        enableEditing: (row) => row.index in rowSelection,
        muiEditTextFieldProps: ({cell}) => ({
          onKeyDown: (e) => {
            if (e.code == "Tab") {
              e.stopPropagation()
              e.preventDefault()
            }
          },
          onBlur: (event) => {
            handleSaveCell(cell, event.target.value)
          }
        })
      }
    ], [selectFormatAndNameTableData, rowSelection]
  )

  return (
    <ArgosFlexbox vertical style={{height: '100%', width: '100%'}}>
      <Loader isLoading={isLoadingGetReportFormats}>
        {!multiSelect && <ArgosFlexbox vertical style={{flex: 1, padding: "20px"}} alignItems="center">
          <ArgosFlexbox vertical style={{flex: 1, width: "100%"}}>
            <ArgosTextField
              required
              key={'reportName'}
              onChange={(event) => {
                setReportName(event.target.value)
              }}
              value={reportName}
              label={'Report Name'}
              style={{paddingBottom: "20px"}}
            />
            <FormControl>
              <FormLabel id="report-format-form-group-label">Report Format</FormLabel>
              <RadioGroup
                aria-labelledby="report-format-form-group-label"
                name="report-format-form-group"
                value={reportFormat}
                onChange={handleReportFormatChange}
              >
                {reportFormats.map((reportFormat: string) => (
                  <FormControlLabel value={reportFormat} control={<Radio/>} label={reportFormat}/>
                ))}
              </RadioGroup>
            </FormControl>
          </ArgosFlexbox>
        </ArgosFlexbox>}
        {multiSelect && <ArgosFlexbox vertical style={{flex: 1, padding: "20px"}} alignItems="center">
          <ArgosFlexbox vertical style={{flex: 1, width: "100%"}}>
            <ArgosTableSimple
              columns={selectFormatAndNameTableColumns}
              data={selectFormatAndNameTableData}

              /** State management */
              layoutMode="semantic"
              state={{rowSelection}}

              /** Editing */
              enableEditing
              editDisplayMode="cell"

              /** Row selection */
              enableRowSelection={true}
              enableStickyHeader={true}
              onRowSelectionChange={setRowSelection}
            />
          </ArgosFlexbox>
        </ArgosFlexbox>}
      </Loader>
    </ArgosFlexbox>
  )
}
